// web/src/tiferet/Tiferet.tsx — תפארת, העוזרת של הצוות (29.09 · build-plan-assistant.md · חוזה tf 0.2.0).
// 📄 מוטי 29.09: "נלך על תפארת וההגדרה שלה — היא העוזרת" · "רק הצוות של עתידה ואני… לא עבור הלקוחות".
// 📄 מוטי 29.09 לילה: "יש משהו לא קוהרנטי בתפארת. היא נמצאת ליד כפתור 'סיור קצר'. הסיור צריך להיות בתוך תפארת כהצעה.
//    ברשגד חילקנו את החלון של העוזר האישי לשניים: העוזר + הערות. אפשר ללחוץ ולבחור באיזה מצב אנחנו עכשיו.
//    העוזר גם יודע לכתוב הערות." ⇒ המבנה של "היועץ" ברשגד (ds/guide.js):
// 🔑 חלון אחד (⛔ חוסם את המסך), שני מצבים בולטים: "שאלה לתפארת — תשובה עכשיו" · "הערה למוטי — נשלח לטיפול" (כלי ההערות
//    הקיים, אותם כלים, בתוך החלון).
// 🔑 במצב שאלה: שורת פעולות (סיור במסך · מה חדש · בדיקות · כל הסיורים · לרשום את השיחה כהערה · שיחה חדשה) → השיחה → שדה
//    הכתיבה מקובע למטה (כתיבה או 🎙). תשובה יכולה לכלול כפתורים: התחלת סיור · מעבר למסך · "הראי לי שוב" · "✓ נרשם כהערה".
// 🔑 ההצעות (סיור במסך שעוד לא ראתה · "יש N חדשים") — הודעה של תפארת בתוך השיחה, עם כפתור. ⛔ שום דבר ליד הכדור חוץ מנקודה.
// 🔑 היכרות ראשונה: נפתחת לבד פעם אחת ומדברת מיד (או בנגיעה הראשונה — הדפדפן חוסם קול לפני נגיעה).
// 🔑 הלשון: השרת שולח את הסיורים כבר בלשון של מי שמחוברת (מוטי — זכר, הצוות — נקבה) · כאן G(נקבה, זכר) לטקסטים של החלון.
// 🔑 כל כשל של תפארת (404 · השרת כבוי · TIFERET_OFF) ⇒ כפתור ההערות הרגיל. ⛔ המסך נשבר.
// 🔑 קישור ישיר: ?tiferet=tour — מתחיל את הסיור של המסך.
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { dk, tf, toApiError } from "../api/client";
import type { components } from "../api/gen/tf";
import { FeedbackButton } from "../components/FeedbackButton";
import { Glow } from "./Glow";
import { TourRunner, findTarget, playable, type Step } from "./TourRunner";
import * as voice from "./voice";
import "./tiferet.css";

type State = components["schemas"]["TiferetState"];
type Tour = components["schemas"]["Tour"];
type Act = components["schemas"]["ChatAction"];
type ChatSummary = components["schemas"]["ChatSummary"];
// 🆕 01.10 — 📄 מוטי: "לתמיד, מסודר לפי חודשים וזמנים" ⇒ ההיסטוריה מקובצת לפי חודש, ולכל שיחה יום, תאריך ושעה.
const monthOf = (iso: string) => new Date(iso).toLocaleDateString("he-IL", { month: "long", year: "numeric" });
const whenOf = (iso: string) =>
  new Date(iso).toLocaleString("he-IL", { weekday: "short", day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" });
type Mode = "all" | "new" | "check";
type Run = { tour: Tour; steps: Step[]; index: number; paused: boolean; mode: Mode | "point" };
type Msg = { role: "user" | "assistant"; text: string; actions?: Act[]; voice?: string | null; offer?: string };

/** הסדר של הקבוצות — אותו סדר של AREAS בשרת (tf/tours.py). */
// 🆕 27.10 — "היכרות": סיור המערכת (system) — מעבר על התפריט העליון מכל מסך, בלי לעבור מסך.
const SYSTEM_TOOL = "system";
const AREA_ORDER = ["היכרות", "השולחן", "היומן", "הגדרות היומן", "לקוחות", "הודעות", "סטודיו", "שותפות", "דיוור", "העסק והאתר",
  "הצד של מוטי"];   // 🆕 01.10 · 🆕 04.10 — דיוור · העסק והאתר
const WIDE_KEY = "atida.tiferet.wide";
// 🆕 30.09 — גרירה. מיקום לכל מצב (קומפקטי / מוגדל), משוחזר רק אם רוחב המסך השתנה בפחות מ-200px (כמו Drag ברשגד).
type Pos = { x: number; y: number; vw: number };
const DRAG_MIN_W = 640;                                        // בטלפון ⛔ גרירה — החלון כבר ברוחב המסך
const posKey = (w: boolean) => `atida.tiferet.pos.${w ? "wide" : "compact"}`;
function readPos(w: boolean): Pos | null {
  try {
    const p = JSON.parse(window.localStorage.getItem(posKey(w)) || "null") as Pos | null;
    return p && typeof p.x === "number" && typeof p.y === "number" && Math.abs((p.vw || 0) - window.innerWidth) <= 200 ? p : null;
  } catch { return null; }
}
function writePos(w: boolean, p: Pos | null) {
  try { if (p) window.localStorage.setItem(posKey(w), JSON.stringify(p)); else window.localStorage.removeItem(posKey(w)); } catch { /* חסום */ }
}                          // 🆕 30.09 — מצב מוגדל נשמר במכשיר (כמו בפלטפורמה לאמניות)
const LOG_KEY = "atida.tiferet.log";                            // 🆕 30.09 — ⛔ בשימוש: השיחה חיה רק בזיכרון (שיחה חדשה בכל כניסה). נמחק פעם אחת.
const MAX_REC_SEC = 120;

const matches = (t: Tour, path: string) => { try { return new RegExp(t.match).test(path); } catch { return false; } };

function useVoiceTick() {
  const [, force] = useState(0);
  useEffect(() => voice.subscribe(() => force((n) => n + 1)), []);
}

/** מה מוצג עכשיו על המסך — תפארת יודעת איפה מדברים איתה (כמו snapshot ברשגד). */
function snapshot(tool: string) {
  const vis = (el: Element) => { const r = el.getBoundingClientRect(); return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < innerHeight; };
  const heads = Array.from(document.querySelectorAll("main h1, main h2, main h3, [role=tab][aria-selected=true]"))
    .filter((el) => !el.closest(".tf-ui, .fb-embedded") && vis(el))
    .map((el) => (el.textContent || "").trim().replace(/\s+/g, " ").slice(0, 60));
  return { path: location.pathname, title: document.title.slice(0, 200), section: Array.from(new Set(heads)).slice(0, 4).join(" › ").slice(0, 400), tool, step: "" };
}

export function Tiferet({ limited = false }: { limited?: boolean }) {
  const loc = useLocation();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [st, setSt] = useState<State | null | "off">(null);
  const [panel, setPanel] = useState(false);
  const [mode, setMode] = useState<"ask" | "note">("ask");
  const [log, setLog] = useState<Msg[]>([]);                    // 📄 מוטי 30.09: "בכל כניסה מחודשת לאתר תופיע שיחה חדשה" ⇒ ⛔ נשמר בלשונית
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState<"" | "think" | "note" | "listen" | "stt">("");
  const [recSec, setRecSec] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const [prefill, setPrefill] = useState<{ key: number; text: string; context: string } | null>(null);
  const [pendingCheck, setPendingCheck] = useState<{ tool: string; step: string } | null>(null);
  const [unseen, setUnseen] = useState(0);
  const [run, setRun] = useState<Run | null>(null);
  // 🆕 30.09 — הבמה של ההצגה העצמית (כמו בפלטפורמה לאמניות). 📄 מוטי: "מאשר הכל, כולל ההצגה העצמית."
  //    בכניסה הראשונה: רקע מוחשך ומטושטש · החלון ריק — שמש גדולה וטקסט · קול חסום ⇒ "▶ להקשבה" · הכפתורים רק אחרי ששמעה עד הסוף ·
  //    "ראתה את ההיכרות" נרשם רק אז ⇒ סגירה באמצע = ההיכרות מההתחלה בכניסה הבאה.
  const [stage, setStage] = useState<{ text: string; voice: string | null; offer: string } | null>(null);
  const [stageHeard, setStageHeard] = useState(false);
  const idleRef = useRef(-1);                                    // המשפט האחרון שהתנגן בנגיעה בשמש (⛔ אותו פעמיים ברצף)
  // 🆕 30.09 — שמירת שיחות (הגירה 095). 📄 מוטי: "מאשר הגירה לשמירת שיחות תפארת והיסטוריית שיחות בחלון".
  //    המזהה נוצר כאן בהודעה הראשונה של המשתמשת ⇒ ⛔ שיחות ריקות · כל שינוי בשיחה ⇒ שמירה (מושהית מעט) · שיחה של מישהי
  //    אחרת (מנהלת-על) = לקריאה בלבד.
  const [chatId, setChatId] = useState<string | null>(null);
  const [chatOwner, setChatOwner] = useState<string | null>(null);   // null = שלי · אחרת — של מי (לקריאה בלבד)
  const [showChats, setShowChats] = useState(false);
  const [chats, setChats] = useState<ChatSummary[] | null>(null);
  const [chatsMore, setChatsMore] = useState(false);   // 🆕 01.10 — יש שיחות ישנות יותר
  const [renaming, setRenaming] = useState<{ id: string; text: string } | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const saveTimer = useRef<number | null>(null);
  // 🆕 30.09 — 📄 מוטי: "צריך מצב קומפקטי ומצב מוגדל כמו ברשגד וכמו ביועצת בפלטפורמת האומניות".
  const [wide, setWide] = useState<boolean>(() => { try { return window.localStorage.getItem(WIDE_KEY) === "1"; } catch { return false; } });
  // 🆕 30.09 — 📄 מוטי: "החלון של תפארת צריך להגרר על המסך בחופשיות, כדי שאפשר יהיה למקם אותו איפה שרוצים מבלי שיפריע לתוכן העמוד."
  //    לפי Drag ברשגד (ערכת העוזרת, 17-window-placement): גרירה מההדר (⛔ מכפתור) · סף 6px · גבולות 8px · מיקום נשמר לכל מצב ·
  //    לחיצה כפולה על ההדר = חזרה למקום הרגיל · בטלפון ⛔ · הבמה של ההצגה העצמית תמיד במרכז.
  const [pos, setPos] = useState<Pos | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ sx: number; sy: number; ox: number; oy: number; moved: boolean } | null>(null);
  const reopen = useRef(false);                                  // החלון נסגר בשביל הצבעה / סיור ⇒ חוזר בסוף
  const startRef = useRef<(mode: Mode) => void>(() => undefined);
  const logEnd = useRef<HTMLDivElement>(null);
  const recRef = useRef<{ mr: MediaRecorder; stream: MediaStream; timer: number } | null>(null);
  useVoiceTick();

  useEffect(() => {
    tf.GET("/staff/tiferet").then(({ data }) => setSt(data && data.enabled ? data : "off")).catch(() => setSt("off"));
  }, []);
  useEffect(() => { try { window.sessionStorage.removeItem(LOG_KEY); } catch { /* חסום */ } }, []);   // שאריות מהגרסה הקודמת
  useEffect(() => { if (panel && mode === "ask") logEnd.current?.scrollIntoView({ block: "end" }); }, [log, panel, mode, busy]);

  const s = st && st !== "off" ? st : null;
  const G = (f: string, m: string) => (s?.gender === "m" ? m : f);
  const tour = useMemo(() => s?.tours.find((t) => matches(t, loc.pathname)) ?? null, [s, loc.pathname]);
  const seen = tour ? s?.progress[tour.tool]?.seen_version ?? 0 : 0;
  const fresh = tour && seen > 0 ? tour.steps.filter((x) => x.since > seen && x.kind !== "check") : [];
  const checks = tour ? tour.steps.filter((x) => x.kind === "check") : [];
  const checksDone = tour ? checks.filter((x) => s?.progress[tour.tool]?.checks[x.id]).length : 0;
  const offer = !!tour && (seen === 0 || fresh.length > 0);

  function push(m: Msg) { setLog((l) => [...l, m]); }

  // ── ההצעה של המסך — הודעה של תפארת בשיחה (פעם אחת לכל מסך ולכל גרסה, בלשונית הזו) ──
  function offerFor(t: Tour): Msg | null {
    const sv = s?.progress[t.tool]?.seen_version ?? 0;
    const nw = sv > 0 ? t.steps.filter((x) => x.since > sv && x.kind !== "check") : [];
    const key = `${t.tool}@${t.version}`;
    if (log.some((m) => m.offer === key)) return null;
    if (sv === 0) return { role: "assistant", text: t.hello, voice: t.voice.hello, offer: key, actions: [{ type: "tour", mode: "full" }] };
    if (nw.length) {
      const n = nw.length === 1 ? "דבר אחד חדש" : `${nw.length} דברים חדשים`;
      return { role: "assistant", text: `יש ${n} ב${t.title} מאז הסיור הקודם. ${G("רוֹצָה", "רוֹצֶה")} שאראה ${G("לָךְ", "לְךָ")}?`,
               offer: key, actions: [{ type: "tour", mode: "new" }] };
    }
    return null;
  }

  // ── היכרות ראשונה — במה, במסך הראשון עם סיור ──
  useEffect(() => {
    if (!s || s.intro_seen || !tour || panel || run) return;
    setSt({ ...s, intro_seen: true });                    // בביקור הזה — פעם אחת. ⛔ נרשם בשרת עד שנשמעה עד הסוף.
    const v = tour.voice.hello_first ?? null;
    setStageHeard(!s.voice || !v || voice.isMuted());     // בלי קול ⇒ הטקסט והכפתורים מיד
    setStage({ text: tour.hello_first, voice: v, offer: `${tour.tool}@${tour.version}` });
    setMode("ask"); setPanel(true);
    // 📄 מוטי 29.09: "הפנייה חייבת להתנגן מיד כשהיא נכנסת בפעם הראשונה, ותפארת צריכה להיות פתוחה."
    if (s.voice && v) void voice.playWhenAllowed(v);
  }, [s, tour]);                                           // eslint-disable-line react-hooks/exhaustive-deps

  // ── הבמה: "נשמעה" רק כשההקלטה הסתיימה (ended) ──
  // 🔴 30.09 (ביקורת ערכת העוזרת, 06-first-intro): קודם נגזר ממעבר speaking() ל-null — שקורה גם כשהדפדפן חוסם את הקול או
  //    שההורדה נכשלה ⇒ הכפתורים הופיעו וההיכרות סומנה בלי שנשמעה. עכשיו רק voice.lastEnded() — נקבע באירוע ended בלבד.
  const endedNow = voice.lastEnded();
  useEffect(() => {
    if (stage && !stageHeard && stage.voice && endedNow === stage.voice) setStageHeard(true);
  }, [endedNow, stage, stageHeard]);
  useEffect(() => {
    if (stage && stageHeard) void tf.POST("/staff/tiferet/progress", { body: { tool: "_intro" } }).catch(() => undefined);
  }, [stage, stageHeard]);

  // ── שמירת השיחה במסד — רק מההודעה הראשונה של המשתמשת, רק שיחה שלי, 700ms אחרי השינוי האחרון ──
  useEffect(() => {
    if (chatOwner || !log.some((m) => m.role === "user")) return;
    let id = chatId;
    if (!id) { id = crypto.randomUUID(); setChatId(id); }
    const cid = id;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      const msgs = log.slice(-200).map((m) => ({
        role: m.role, text: m.text.slice(0, 8000),
        ...(m.actions?.length ? { actions: m.actions.slice(0, 12) } : {}),
        ...(m.voice ? { voice: m.voice } : {}), ...(m.offer ? { offer: m.offer.slice(0, 80) } : {}),
      }));
      void tf.PUT("/staff/tiferet/chats/{chat_id}", { params: { path: { chat_id: cid } }, body: { page: (tour?.title ?? "").slice(0, 80), msgs } })
        .then(({ data }) => { if (data) setChats((l) => (l ? [data, ...l.filter((c) => c.id !== data.id)] : l)); })
        .catch(() => undefined);                       // ⛔ שבירה: השיחה ממשיכה בחלון גם אם השמירה נכשלה
    }, 700);
  }, [log]);                                               // eslint-disable-line react-hooks/exhaustive-deps

  // ── גרירה: המיקום השמור של המצב הנוכחי · התאמה לשינוי גודל המסך ──
  const wideNow = wide && typeof window !== "undefined" && window.innerWidth >= DRAG_MIN_W;
  function clampPos(x: number, y: number): Pos {
    const r = panelRef.current?.getBoundingClientRect();
    const w = r?.width ?? 440, h = r?.height ?? 600;
    return { x: Math.round(Math.min(Math.max(8, x), Math.max(8, window.innerWidth - w - 8))),
             y: Math.round(Math.min(Math.max(8, y), Math.max(8, window.innerHeight - h - 8))), vw: window.innerWidth };
  }
  useEffect(() => {
    if (panel) setPos(window.innerWidth >= DRAG_MIN_W ? readPos(wideNow) : null);
  }, [panel, wideNow]);                                    // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pos) return;
    const fit = () => { if (window.innerWidth < DRAG_MIN_W) setPos(null); else setPos((q) => (q ? clampPos(q.x, q.y) : q)); };
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [!!pos]);                                             // eslint-disable-line react-hooks/exhaustive-deps

  // ── מוגדל: השיחות הקודמות בעמודה קבועה בצד ⇒ נטענות כשהחלון נפתח ──
  useEffect(() => {
    if (!panel || !wide || chats !== null) return;
    void loadChats();
  }, [panel, wide]);                                       // eslint-disable-line react-hooks/exhaustive-deps

  // ── פתיחת החלון במסך שיש בו מה להציע ⇒ ההצעה נכנסת לשיחה ──
  useEffect(() => {
    if (!s || !panel || mode !== "ask" || !tour || run || stage) return;
    const o = offerFor(tour);
    if (o) { push(o); if (o.voice && s.voice && voice.canAutoplay()) void voice.play(o.voice); }
  }, [panel, mode, tour, s?.progress, stage]);             // eslint-disable-line react-hooks/exhaustive-deps

  // ── קישור ישיר ?tiferet=tour ──
  useEffect(() => {
    if (!s || !tour || params.get("tiferet") !== "tour") return;
    const next = new URLSearchParams(params); next.delete("tiferet");
    setParams(next, { replace: true });
    // 🔴 29.09: ⛔ ניקוי ב-return — setParams מריץ את האפקט שוב והניקוי ביטל את הטיימר. startTour דרך ref.
    window.setTimeout(() => startRef.current("all"), 600);
  }, [s, tour, params]);                                   // eslint-disable-line react-hooks/exhaustive-deps

  // ── מעבר למסך אחר באמצע סיור ⇒ הסיור נעצר ──
  useEffect(() => {
    // 🆕 27.10 — סיור המערכת עובר בין מסכים בעצמו ⇒ ⛔ נעצר במעבר
    if (run && run.mode !== "point" && run.tour.tool !== SYSTEM_TOOL && !matches(run.tour, loc.pathname)) { voice.stop(); setRun(null); }
  }, [loc.pathname]);                                      // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => stopRec(true), []);                // eslint-disable-line react-hooks/exhaustive-deps

  if (st === null) return null;                                   // טוען — ⛔ כפתור שמופיע ונעלם
  if (st === "off") return <FeedbackButton />;                    // כל כשל ⇒ כלי ההערות הרגיל

  function saveProgress(body: { tool: string; seen_version?: number; check?: { step: string; ok: boolean; feedback_id?: string | null } }) {
    return tf.POST("/staff/tiferet/progress", { body }).then(({ data }) => {
      if (data) setSt((x) => (x && x !== "off" ? { ...x, progress: { ...x.progress, [data.tool]: { seen_version: data.seen_version, checks: data.checks } } } : x));
    }).catch(() => undefined);
  }

  function openPanel(m: "ask" | "note" = mode) { setMode(m); setPanel(true); }
  function closePanel() {
    voice.stop(); stopRec(true); setPanel(false);
    setStage(null);                                                        // סגירה באמצע הבמה ⇒ ⛔ נרשם ⇒ מההתחלה בכניסה הבאה
    if (run?.paused) { setPendingCheck(null); resumeAfterCheck(); }       // בדיקה שנכשלה ⇒ סוגרים בלי לשלוח ⇒ הסיור ממשיך
  }

  /** יציאה מהבמה אחרי ששמעה: ההיכרות נשארת כתובה בשיחה (עם ההצעה לסיור), ואז — סיור או שיחה. */
  function leaveStage(then: "tour" | "chat") {
    if (!stage) return;
    const st0 = stage;
    setStage(null);
    push({ role: "assistant", text: st0.text, voice: st0.voice, offer: st0.offer, actions: [{ type: "tour", mode: "full" }] });
    if (then === "tour") startTour("all");
  }

  // ════ סיורים ════
  startRef.current = startTour;
  function startTour(m: Mode, only?: Tour) {
    const t = only ?? tour;                                   // 🆕 27.10 — only = סיור המערכת, מכל מסך
    if (!t) return;
    voice.stop();
    const base = only ? only.steps : m === "new" ? fresh : m === "check" ? checks : t.steps;
    const steps = only ? base : playable(base);                // סיור המערכת: השלבים במסכים אחרים — לא נבדקים מכאן
    if (!steps.length) {
      openPanel("ask");
      push({ role: "assistant", text: m === "new" ? "אין כאן שום דבר חדש מאז הסיור הקודם." : "אין סיור למסך הזה עדיין." });
      return;
    }
    reopen.current = true;
    setShowAll(false);
    setPanel(false);
    setRun({ tour: t, steps, index: 0, paused: false, mode: m });
  }

  /** 🆕 27.10 — מתוך סיור המערכת: "סיור מפורט בעמוד הזה" ⇒ הסיור הכללי נעצר והסיור של המסך מתחיל. */
  function deepTour(tool: string) {
    voice.stop(); setRun(null); reopen.current = false;
    const t = s?.tours.find((x) => x.tool === tool);
    if (!t) return;
    if (t === tour) window.setTimeout(() => startRef.current("all"), 300);
    else nav(`${t.href}?tiferet=tour`);
  }

  function endTour(finished: boolean) {
    const r = run;
    setRun(null);
    if (!r) return;
    const back = reopen.current;
    reopen.current = false;
    if (r.mode === "point") { if (back) setPanel(true); return; }
    if (finished && r.mode !== "check") void saveProgress({ tool: r.tour.tool, seen_version: r.tour.version });
    if (!finished) return;                                        // נסגר באמצע — ⛔ פותחים חלון
    setMode("ask"); setPanel(true);
    const done = s!.progress[r.tour.tool]?.checks ?? {};
    const left = r.tour.steps.filter((x) => x.kind === "check" && !done[x.id]).length;
    const text = r.mode === "check"
      ? (left ? `עברנו על הבדיקות. ${left === 1 ? "נשארה אחת" : `נשארו ${left}`} ${G("שלא סִימַּנְתְּ", "שלא סִימַּנְתָּ")}.`
              : "כל הבדיקות במסך הזה סומנו. תודה! מה שלא עבד כבר אצל מוטי.")
      : "זה הסיור. " + (left ? `${G("רוֹצָה", "רוֹצֶה")} לעבור על ${left === 1 ? "הבדיקה" : `${left} הבדיקות`} של המסך? ככה מוטי יודע מה עובד.`
                             : "אם משהו לא ברור, אפשר פשוט לשאול אותי.");
    push({ role: "assistant", text, actions: r.mode !== "check" && left ? [{ type: "tour", mode: "check" }] : [] });
    if (s!.voice) void voice.say(text);
  }

  function onCheck(step: Step, ok: boolean, index: number) {
    if (!run) return;
    void saveProgress({ tool: run.tour.tool, check: { step: step.id, ok } });
    if (ok) return;
    voice.stop();
    setPendingCheck({ tool: run.tour.tool, step: step.id });
    setPrefill({ key: Date.now(), text: `בדיקה בסיור "${run.tour.title}" — ${step.title}: לא עובד. `,
                 context: `מתפארת: בדיקה בסיור ${run.tour.tool} · שלב ${step.id} (${step.title})` });
    setRun({ ...run, index, paused: true });
    openPanel("note");
  }

  function resumeAfterCheck() {
    if (!run?.paused) return;
    const nextIndex = run.index + 1;
    if (nextIndex < run.steps.length) { setPanel(false); setRun({ ...run, index: nextIndex, paused: false }); }
    else endTour(true);
  }

  /** תפארת מצביעה על חלק במסך באמצע שיחה (הכישור highlight). */
  function point(a: Act) {
    if (!tour) return;
    const base = tour.steps.find((x) => x.id === a.step);
    if (!base) return;
    voice.stop();
    reopen.current = panel;
    if (innerWidth < 640 || findTarget(base.sel)) setPanel(false);
    setRun({ tour, steps: [{ ...base, kind: "show", text: a.text || base.text, voice: a.text ? null : base.voice }], index: 0, paused: false, mode: "point" });
  }

  // ════ שיחה ════
  // 🆕 01.10 — גם מה שהוצג בכל תשובה (כפתור, הצבעה, סיור, הערה), כדי שהמוח לא יחזור על זה בשאלה הבאה.
  const hist = (n: number) => log.slice(-n).map((m) => ({ role: m.role, text: m.text.slice(0, 4000),
    ...(m.actions?.length ? { actions: m.actions.slice(0, 12) } : {}) }));

  async function ask(text: string) {
    const q = text.trim();
    if (!q || busy === "think" || chatOwner) return;          // שיחה של מישהי אחרת — לקריאה בלבד
    voice.stop();
    const history = hist(16);
    push({ role: "user", text: q });
    setDraft(""); setBusy("think");
    const { data, error, response } = await tf.POST("/staff/tiferet/chat", {
      body: { text: q.slice(0, 2000), history, page: snapshot(tour?.tool ?? "") },
    }).catch(() => ({ data: undefined, error: undefined, response: { status: 0 } as Response }));
    setBusy("");
    if (!data) {
      push({ role: "assistant", text: response.status ? toApiError(response.status, error).message : "אין חיבור כרגע. אפשר לנסות שוב בעוד רגע." });
      return;
    }
    push({ role: "assistant", text: data.text, actions: data.actions });
    if (s!.voice) void voice.say(data.text);
    const hl = data.actions.find((a) => a.type === "highlight");
    if (hl) window.setTimeout(() => point(hl), 350);
  }

  async function logAsNote() {
    if (busy || !log.some((m) => m.role === "user")) return;
    setBusy("note");
    const { data, error, response } = await tf.POST("/staff/tiferet/note", { body: { history: hist(40), page: snapshot(tour?.tool ?? "") } })
      .catch(() => ({ data: undefined, error: undefined, response: { status: 0 } as Response }));
    setBusy("");
    if (!data) { push({ role: "assistant", text: response.status ? toApiError(response.status, error).message : "לא נשמר — אין חיבור." }); return; }
    push({ role: "assistant", text: `רשמתי את השיחה כהערה למוטי:\n${data.text}`, actions: [{ type: "note", id: data.id }] });
    if (s!.voice) void voice.say("רשמתי את השיחה כהערה למוטי.");
  }

  function toggleWide() {
    setWide((w) => {
      const v = !w;
      try { if (v) window.localStorage.setItem(WIDE_KEY, "1"); else window.localStorage.removeItem(WIDE_KEY); } catch { /* חסום */ }
      return v;
    });
    setShowChats(false); setRenaming(null); setConfirmDel(null);
  }

  // ════ גרירת החלון מההדר ════
  function onHeadDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0 || window.innerWidth < DRAG_MIN_W || (e.target as Element).closest("button, a, input, textarea")) return;
    const r = panelRef.current?.getBoundingClientRect();
    if (!r) return;
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: r.left, oy: r.top, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function onHeadMove(e: React.PointerEvent<HTMLDivElement>) {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < 6) return;       // סף — נגיעה קצרה אינה גרירה
    d.moved = true;
    setPos(clampPos(d.ox + dx, d.oy + dy));
  }
  function onHeadUp(e: React.PointerEvent<HTMLDivElement>) {
    const d = dragRef.current;
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* כבר שוחרר */ }
    if (d?.moved) setPos((q) => { if (q) writePos(wideNow, q); return q; });
  }
  function onHeadDouble(e: React.MouseEvent<HTMLDivElement>) {
    if ((e.target as Element).closest("button, a, input, textarea")) return;
    setPos(null); writePos(wideNow, null);                  // חזרה למקום הרגיל
  }

  // ════ שיחות קודמות (30.09 · בהשראת "ארכיון השיחות" בפלטפורמה לאמניות) ════
  // 🆕 01.10 — 50 בכל פעם; "שיחות ישנות יותר" ממשיך מהאחרונה ברשימה.
  async function loadChats(more = false) {
    const before = more ? chats?.[chats.length - 1]?.updated_at : undefined;
    const { data } = await tf.GET("/staff/tiferet/chats", { params: { query: before ? { before } : {} } })
      .catch(() => ({ data: undefined }));
    const items = data?.items ?? [];
    setChats((l) => (more ? [...(l ?? []), ...items] : items));
    setChatsMore(!!data?.more);
  }
  function toggleChats() {
    const open = !showChats;
    setShowChats(open); setShowAll(false); setRenaming(null); setConfirmDel(null);
    if (open) void loadChats();
  }
  function newConversation() {
    voice.stop(); stopRec(true);
    setLog([]); setChatId(null); setChatOwner(null); setShowChats(false); setShowAll(false);
  }
  async function openChat(c: ChatSummary) {
    voice.stop(); stopRec(true);
    const { data } = await tf.GET("/staff/tiferet/chats/{chat_id}", { params: { path: { chat_id: c.id } } }).catch(() => ({ data: undefined }));
    if (!data) { push({ role: "assistant", text: "לא הצלחתי לפתוח את השיחה. אפשר לנסות שוב." }); setShowChats(false); return; }
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    setChatId(data.id); setChatOwner(data.mine ? null : (data.owner || "הצוות"));
    setLog(data.msgs.map((m) => ({ role: m.role, text: m.text, actions: m.actions, voice: m.voice ?? null, offer: m.offer ?? undefined })));
    setShowChats(false);
  }
  async function renameChat(id: string, title: string) {
    setRenaming(null);
    const { data } = await tf.PATCH("/staff/tiferet/chats/{chat_id}", { params: { path: { chat_id: id } }, body: { title: title.trim() || null } })
      .catch(() => ({ data: undefined }));
    if (data) setChats((l) => l?.map((c) => (c.id === id ? data : c)) ?? l);
  }
  async function deleteChat(id: string) {
    setConfirmDel(null);
    const { response } = await tf.DELETE("/staff/tiferet/chats/{chat_id}", { params: { path: { chat_id: id } } })
      .catch(() => ({ response: { ok: false } as Response }));
    if (!response.ok) return;
    setChats((l) => l?.filter((c) => c.id !== id) ?? l);
    if (id === chatId) newConversation();
  }

  // ── 🎙 דיבור אליה: הקלטה → התמלול של כלי ההערות → שאלה ──
  async function startRec() {
    if (recRef.current) { stopRec(false); return; }
    voice.stop();
    let stream: MediaStream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } }); }
    catch { push({ role: "assistant", text: "אין לי גישה למיקרופון. אפשר לאשר אותו בהגדרות הדפדפן, או לכתוב לי." }); return; }
    const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) ?? "";
    const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks: Blob[] = [];
    const t0 = Date.now();
    mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    mr.onstop = () => { void transcribe(new Blob(chunks, { type: mr.mimeType || mime || "audio/webm" }), (Date.now() - t0) / 1000); };
    mr.start(500);
    const timer = window.setInterval(() => { const sec = Math.floor((Date.now() - t0) / 1000); setRecSec(sec); if (sec >= MAX_REC_SEC) stopRec(false); }, 300);
    recRef.current = { mr, stream, timer };
    setRecSec(0); setBusy("listen");
  }
  function stopRec(discard: boolean) {
    const r = recRef.current;
    if (!r) return;
    recRef.current = null;
    window.clearInterval(r.timer);
    if (discard) r.mr.onstop = null;
    if (r.mr.state !== "inactive") r.mr.stop();
    r.stream.getTracks().forEach((t) => t.stop());
    setBusy(discard ? "" : "stt");
  }
  async function transcribe(blob: Blob, secs: number) {
    if (secs < 0.8 || blob.size < 1500) { setBusy(""); return; }
    const buf = new Uint8Array(await blob.arrayBuffer());
    let bin = "";
    for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
    const { data } = await dk.POST("/staff/feedback/transcribe", { body: { audio_base64: btoa(bin), mime_type: blob.type || "audio/webm" } })
      .catch(() => ({ data: undefined }));
    setBusy("");
    const t = data?.text?.trim();
    if (t) void ask(t);
    else push({ role: "assistant", text: "לא שמעתי מילים. אפשר לנסות שוב, או לכתוב לי." });
  }

  function onAction(a: Act) {
    if (a.type === "tour") return startTour(a.mode === "new" ? "new" : a.mode === "check" ? "check" : "all");
    if (a.type === "navigate" && a.href) { voice.stop(); nav(a.href); return; }
    if (a.type === "highlight") return point(a);
  }

  // ════ תצוגה ════
  const muted = voice.isMuted();
  const speakingNow = voice.speaking();
  const sysTour = limited ? undefined : s!.tours.find((t) => t.tool === SYSTEM_TOOL);
  const listable = s!.tours.filter((t) => !t.detail && (!limited || t.area === "סטודיו"));
  const seenCount = listable.filter((t) => (s!.progress[t.tool]?.seen_version ?? 0) >= t.version).length;
  const hasChat = log.some((m) => m.role === "user");
  const isWide = wide && !stage && window.innerWidth >= 640;       // בטלפון ⛔ מוגדל (החלון כבר ברוחב המסך)
  // 🆕 30.09 — 📄 מוטי: "ברשגד יצרנו 5-6 משפטים שמתנגנים כשלוחצים על האנימציה של תפארת".
  //    📄 מוטי 30.09 11:57: "ליד כל בועת טקסט יש כפתור השמעה — לכן [השמעה חוזרת בשמש] לא נדרשת יותר. בלחיצה על תפארת היא תגיד
  //    משפט אקראי שפונה אל המשתמש." ⇒ נגיעה בשמש: מדברת → עצירה · אחרת → אחד המשפטים (אקראי, ⛔ אותו פעמיים ברצף).
  //    בקול החי (/say) — נשמר בדפדפן עד סוף הביקור ⇒ כל משפט עולה פעם אחת לביקור.
  // 🆕 30.09 אחה"צ — המשפטים מגיעים מהשרת (tf/tours.py IDLE), בלשון של מי שמחוברת, עם מפתח הקלטה ⇒ מוקלטים פעם אחת.
  const idleLines = s!.idle;
  function onSun() {
    if (speakingNow) { voice.stop(); return; }
    if (!s!.voice || muted) return;
    if (!idleLines.length) return;
    let i = Math.floor(Math.random() * idleLines.length);
    if (i === idleRef.current) i = (i + 1) % idleLines.length;
    idleRef.current = i;
    const line = idleLines[i];
    if (line) void voice.play(line.voice);
  }
  const stateLine = busy === "think" ? "חושבת…" : busy === "note" ? "מסכמת את השיחה…"
    : busy === "listen" ? `מקשיבה… ${Math.floor(recSec / 60)}:${String(recSec % 60).padStart(2, "0")}`
    : busy === "stt" ? "מתמללת…" : speakingNow ? "מדברת…" : voice.waiting() ? "🔊 נגיעה בכל מקום במסך — ואני מדברת" : "העוזרת במערכת";
  // 🆕 30.09 — רמה 2 (קריאה): שתי שאלות על נתונים בראש ההצעות
  const suggestions = s!.brain && tour ? [
    ...(s!.level >= 2 ? ["מה יש ביומן מחר?", "אילו פניות מחכות למועד?"] : []),
    "מה אפשר לעשות במסך הזה?",
    ...tour.steps.filter((x) => x.kind === "show").slice(1, s!.level >= 2 ? 2 : 3).map((x) => `מה זה "${x.title}"?`),
    "משהו לא עובד לי כאן",
  ] : [];

  function chatList(side: boolean) {
    return (
                  <div className="tf-chats">
                    <div className="tf-chats-head">
                      <strong>שיחות קודמות</strong>
                      {side ? <button className="btn btn-secondary btn-sm" onClick={newConversation}>+ שיחה חדשה</button>
                             : <button className="btn btn-quiet btn-sm" onClick={() => setShowChats(false)}>חזרה לשיחה</button>}
                    </div>
                    {chats === null ? <div className="small muted">טוענת…</div>
                      : chats.length === 0 ? <div className="small muted">עוד אין שיחות שמורות. שיחה נשמרת מההודעה הראשונה {G("שֶׁלָּךְ", "שֶׁלְּךָ")}.</div>
                      : chats.map((c, i) => (
                        <Fragment key={c.id}>
                        {(i === 0 || monthOf(chats[i - 1]!.updated_at) !== monthOf(c.updated_at)) &&
                          <div className="tf-chats-month" role="heading" aria-level={3}>{monthOf(c.updated_at)}</div>}
                        <div className={`tf-chat${c.id === chatId ? " is-current" : ""}`}>
                          {renaming?.id === c.id ? (
                            <form className="tf-chat-rename" onSubmit={(e) => { e.preventDefault(); void renameChat(c.id, renaming.text); }}>
                              <label className="visually-hidden" htmlFor={`tf-rn-${c.id}`}>שם לשיחה</label>
                              <input id={`tf-rn-${c.id}`} autoFocus maxLength={80} value={renaming.text} placeholder={c.title}
                                     onChange={(e) => setRenaming({ id: c.id, text: e.target.value })}
                                     onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setRenaming(null); } }} />
                              <button type="submit" className="btn btn-primary btn-sm">שמירה</button>
                            </form>
                          ) : (
                            <button className="tf-chat-open" onClick={() => void openChat(c)}>
                              <b>{c.title}</b>
                              <span className="small">
                                {[whenOf(c.updated_at), c.page, `${c.count} הודעות`]
                                  .filter(Boolean).join(" · ")}
                                {!c.mine && <span className="tf-chat-owner"> · של {c.owner}</span>}
                              </span>
                            </button>
                          )}
                          {c.mine && renaming?.id !== c.id && (
                            confirmDel === c.id ? (
                              <span className="tf-chat-confirm">
                                <span className="small">למחוק?</span>
                                <button className="btn btn-sm tf-danger" onClick={() => void deleteChat(c.id)}>כן</button>
                                <button className="btn btn-quiet btn-sm" onClick={() => setConfirmDel(null)}>לא</button>
                              </span>
                            ) : (
                              <span className="tf-chat-tools">
                                <button className="tf-ib-sm" aria-label="שם לשיחה" title="שם לשיחה"
                                        onClick={() => setRenaming({ id: c.id, text: c.custom_title ? c.title : "" })}>✎</button>
                                <button className="tf-ib-sm" aria-label="מחיקה" title="מחיקה" onClick={() => setConfirmDel(c.id)}>🗑</button>
                              </span>
                            )
                          )}
                        </div>
                        </Fragment>
                      ))}
                    {chats && chatsMore && (
                      <button className="btn btn-quiet btn-sm tf-chats-more" onClick={() => void loadChats(true)}>שיחות ישנות יותר</button>
                    )}
                  </div>
    );
  }

  function msgView(m: Msg, i: number) {
    if (m.role === "user") return <div key={i} className="tf-msg is-user">{m.text}</div>;
    const key = m.voice ?? voice.sayKey(m.text);
    const on = speakingNow === key;
    return (
      <div key={i} className="tf-msg is-her">
        {s!.voice && !muted && (
          <button className="tf-say-btn" aria-label={on ? "לעצור" : "להשמיע שוב"} title={on ? "לעצור" : "להשמיע שוב"}
                  onClick={() => { if (on) voice.stop(); else if (m.voice) void voice.play(m.voice); else void voice.say(m.text); }}>{on ? "■" : "🔈"}</button>
        )}
        {m.text}
        {!!m.actions?.length && (
          <div className="tf-mact">
            {m.actions.map((a, j) => a.type === "tour" && tour ? (
              <button key={j} className="btn btn-primary btn-sm" onClick={() => onAction(a)}>
                ▶ {a.mode === "check" ? "להתחיל את הבדיקות" : a.mode === "new" ? "להראות מה חדש" : `סיור ב${tour.title}`}
              </button>
            ) : a.type === "navigate" ? (
              <button key={j} className="btn btn-secondary btn-sm" onClick={() => onAction(a)}>{a.label} ←</button>
            ) : a.type === "highlight" && a.step ? (
              <button key={j} className="btn btn-quiet btn-sm" onClick={() => onAction(a)}>👁 {G("הראי לי שוב", "הראה לי שוב")}</button>
            ) : a.type === "note" ? (
              <span key={j} className="tf-chip">✓ נרשם כהערה למוטי</span>
            ) : null)}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {/* 🔑 הכדור נשאר גלוי גם בזמן סיור. לחיצה עליו באמצע סיור = עצירה ופתיחת החלון.
          📄 מוטי 30.09 (הערה דרך תפארת): "להסיר את הנקודה הסגולה" · "כשהחלון פתוח — הכדור עובר לחלון" ⇒ החלון פתוח = ⛔ כדור בפינה;
          השמש יושבת בכיפה בראש החלון. ההצעה של המסך — רק הודעה בשיחה (⛔ סימן על הכדור). */}
      {!panel && (
        <div className="tf-ui tf-dock">
          <button className={`tf-orb${speakingNow ? " is-speaking" : ""}`}
                  aria-label={offer ? "תפארת — העוזרת · יש משהו להראות במסך הזה" : "תפארת — העוזרת"} title="תפארת — העוזרת" aria-expanded={false}
                  onClick={() => {
                    if (run?.paused) { openPanel("note"); return; }
                    if (run) { voice.stop(); setRun(null); reopen.current = false; openPanel("ask"); return; }
                    openPanel(unseen > 0 && !offer ? "note" : "ask");
                  }}>
            <Glow size="orb" speaking={!!speakingNow} />
            {unseen > 0 && <span className="fb-count" aria-label={`${unseen} תשובות חדשות ממוטי`}>{unseen}</span>}
          </button>
        </div>
      )}

      {/* הבמה: כל הרקע מוחשך, מטושטש ודהוי — רק תפארת בפוקוס. ⛔ לחיצה על הרקע סוגרת (אי אפשר לדלג בטעות). */}
      {panel && stage && <div className="tf-ui tf-stage-dim" aria-hidden="true" />}

      {panel && (
        <div className={`tf-ui tf-panel${mode === "ask" ? " is-ask" : " is-note"}${stage ? " is-stage" : ""}${isWide ? " is-wide" : ""}${pos && !stage ? " is-moved" : ""}`}
             ref={panelRef} style={pos && !stage ? { left: pos.x, top: pos.y } : undefined} role="dialog" aria-label="תפארת"
             onKeyDown={(e) => { if (e.key === "Escape" && !e.defaultPrevented && mode === "ask") { e.stopPropagation(); closePanel(); } }}>
          {stage ? (
            <div className="tf-stage">
              <button className="tf-ib tf-ib-end" aria-label="סגירה" title="סגירה" onClick={closePanel}>✕</button>
              {/* נגיעה בשמש = ההיכרות שוב מההתחלה */}
              <button type="button" className="tf-stage-sun" aria-label="להשמיע שוב" title="להשמיע שוב"
                      onClick={() => { if (stage.voice && s!.voice && voice.speaking() !== stage.voice) void voice.play(stage.voice); }}>
                <Glow size="stage" speaking={!!speakingNow} />
                {!stageHeard && voice.waiting() && (
                  <span className="tf-stage-listen">▶ להקשבה</span>
                )}
              </button>
              <h2 className="tf-stage-name">תפארת</h2>
              <p className="tf-stage-text">{stage.text}</p>
              {stageHeard ? (
                <div className="tf-stage-acts">
                  {tour && <button className="btn btn-primary" onClick={() => leaveStage("tour")}>▶ סיור ב{tour.title}</button>}
                  <button className="btn btn-secondary" onClick={() => leaveStage("chat")}>אחר כך — לשיחה</button>
                </div>
              ) : (
                <div className="tf-stage-wait small" aria-live="polite">{speakingNow ? "מדברת…" : voice.waiting() ? "נגיעה אחת — ואני מדברת" : "רגע…"}</div>
              )}
            </div>
          ) : (<>
          {/* 📄 מוטי 30.09: "שהאנימציה שלה תהיה בצד ימין של ההדר. מצד שמאל הכותרת, כפתור הרמקול וגם שני הכפתורים שאלה לתפארת
              והערה למוטי… כל הדברים האלו יכנסו יחד באותו הגובה של ההדר" ⇒ נבחרה הצעה ב במוקאפ ("נראית טוב מאוד… עוד טיפה לרווח בין
              השמש לשאר התוכן"). השמש מימין · השם + שורת כלים · מתחתם מתג המצבים. נגיעה בשמש = עצירה / משפט אקראי. */}
          <div className="tf-head" onPointerDown={onHeadDown} onPointerMove={onHeadMove} onPointerUp={onHeadUp}
               onPointerCancel={onHeadUp} onDoubleClick={onHeadDouble}>
            <button type="button" className="tf-sun" aria-label={speakingNow ? "לעצור" : "תפארת — נגיעה ואני אומרת משהו"}
                    title={speakingNow ? "לעצור" : "תפארת"} onClick={() => onSun()}>
              <Glow size="hd" speaking={!!speakingNow} />
            </button>
            <div className="tf-head-main">
              <div className="tf-head-top">
                <div className="tf-head-name">
                  <strong>תפארת</strong>
                  <span className="small tf-state" aria-live="polite">{stateLine}</span>
                </div>
                <div className="tf-head-tools">
                  {s!.voice && (
                    <button className="tf-ib" aria-label={muted ? "להפעיל קול" : "להשתיק"} title={muted ? "להפעיל קול" : "להשתיק"}
                            onClick={() => voice.setMuted(!muted)}>
                      <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" />
                        {muted ? <><path d="M16 9.5l5 5" /><path d="M21 9.5l-5 5" /></> : <><path d="M15.5 9a4 4 0 0 1 0 6" /><path d="M18 6.5a7.5 7.5 0 0 1 0 11" /></>}
                      </svg>
                    </button>
                  )}
                  {mode === "ask" && !isWide && (
                    <button className={`tf-ib${showChats ? " on" : ""}`} aria-label="שיחות קודמות" title="שיחות קודמות"
                            aria-expanded={showChats} onClick={toggleChats}>
                      <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" /><path d="M3 4.5v4h4" /><path d="M12 7.5V12l3 2" /></svg>
                    </button>
                  )}
                  <button className="tf-ib tf-expand" aria-label={isWide ? "הקטנת החלון" : "הגדלת החלון"} title={isWide ? "הקטנת החלון" : "הגדלת החלון"}
                          aria-pressed={isWide} onClick={toggleWide}>
                    {isWide
                      ? <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10h-6V4" /><path d="M14 10l7-7" /><path d="M4 14h6v6" /><path d="M10 14l-7 7" /></svg>
                      : <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M14 4h6v6" /><path d="M20 4l-7 7" /><path d="M10 20H4v-6" /><path d="M4 20l7-7" /></svg>}
                  </button>
                  <button className="tf-ib" aria-label="סגירה" title="סגירה" onClick={closePanel}>
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12" /><path d="M18 6L6 18" /></svg>
                  </button>
                </div>
              </div>
              <div className="tf-modes" role="tablist" aria-label="מה עושים עכשיו">
                <button role="tab" aria-selected={mode === "ask"} className={`tf-mode is-ask${mode === "ask" ? " on" : ""}`}
                        onClick={() => setMode("ask")}>
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3.5l1.8 5.2 5.2 1.8-5.2 1.8L12 17.5l-1.8-5.2-5.2-1.8 5.2-1.8z" /><path d="M19 16v4" /><path d="M17 18h4" /></svg>
                  שאלה לתפארת
                </button>
                <button role="tab" aria-selected={mode === "note"} className={`tf-mode is-note${mode === "note" ? " on" : ""}`}
                        onClick={() => { voice.stop(); stopRec(true); setMode("note"); }}>
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6" /><path d="M10 21h4" /><path d="M12 3a6 6 0 0 0-3.6 10.8c.7.6 1.1 1.3 1.1 2.2h5c0-.9.4-1.6 1.1-2.2A6 6 0 0 0 12 3z" /></svg>
                  הערה למוטי
                  {unseen > 0 && <span className="tf-badge" aria-label={`${unseen} תשובות חדשות`}>{unseen}</span>}
                </button>
              </div>
            </div>
          </div>

          {mode === "ask" ? (
            <div className="tf-body">
              {isWide && <aside className="tf-side" aria-label="שיחות קודמות">{chatList(true)}</aside>}
              <div className="tf-main">
              {/* 📄 מוטי 30.09: "סיור במסך · בדיקות · כל הסיורים · שיחה חדשה — בשורה אחת" · "כפתורי הבועות יוצרים חללים גדולים בעין
                  וחוסר סדר" ⇒ פס אחד שקט, ארבעה חלקים שווים, תמיד באותו מקום (מה שלא זמין — מעומעם, ⛔ נעלם ומזיז את השאר).
                  "מה חדש" = הודעה של תפארת בשיחה (כבר קיים) · "לרשום את השיחה כהערה" = שורה בתוך השיחה, אחרי שיש שיחה. */}
              <div className="tf-acts" role="toolbar" aria-label="פעולות">
                <button disabled={!tour} onClick={() => startTour("all")}>
                  <span className="tf-ai" aria-hidden="true">▶</span>סיור במסך
                </button>
                <button disabled={!checks.length} onClick={() => startTour("check")}>
                  <span className="tf-ai" aria-hidden="true">✓</span>בדיקות
                  {checks.length > 0 && <span className="tf-an">{checksDone}/{checks.length}</span>}
                </button>
                <button aria-expanded={showAll} onClick={() => { setShowAll(!showAll); setShowChats(false); }}>
                  <span className="tf-ai" aria-hidden="true">☰</span>כל הסיורים
                  <span className="tf-an">{seenCount}/{listable.length}</span>
                </button>
                <button disabled={log.length === 0 && !chatId && !showChats} onClick={newConversation}>
                  <span className="tf-ai" aria-hidden="true">↺</span>שיחה חדשה
                </button>
              </div>

              <div className="tf-log" aria-live="polite">
                {showChats && !isWide && chatList(false)}
                {!showChats && chatOwner && (
                  <div className="tf-readonly small">השיחה של {chatOwner} — לקריאה בלבד. להמשך — שיחה חדשה.</div>
                )}
                {!showChats && showAll && (
                  <div className="tf-all">
                    <div className="small muted">{G("רָאִית", "רָאִיתָ")} {seenCount} מתוך {listable.length}. נגיעה במסך — עוברים אליו והסיור מתחיל.</div>
                    {AREA_ORDER.filter((a) => listable.some((t) => t.area === a)).map((a) => (
                      <div key={a} className="tf-area">
                        <div className="small tf-area-title">{a}</div>
                        <div className="row-wrap">
                          {listable.filter((t) => t.area === a).map((t) => {
                            const done = (s!.progress[t.tool]?.seen_version ?? 0) >= t.version;
                            return (
                              <button key={t.tool} className={`tag${done ? " tf-done" : ""}${t === tour ? " tf-here" : ""}`}
                                      onClick={() => { setShowAll(false); if (t.tool === SYSTEM_TOOL) startTour("all", t); else if (t === tour) startTour("all"); else { voice.stop(); nav(`${t.href}?tiferet=tour`); } }}>
                                {done ? "✓ " : ""}{t.title}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {log.length === 0 && !showAll && !showChats && (
                  <div className="tf-msg is-her">
                    {tour ? `אנחנו ב${tour.title}. ` : "במסך הזה עוד אין לי סיור. "}
                    {s!.brain ? `אפשר לשאול אותי כל שאלה על המערכת, בכתב או בקול. ואם משהו לא עובד — ${G("ספרי", "ספר")} לי, ואני ארשום למוטי.`
                              : "אם משהו לא ברור או לא עובד, אפשר לעבור ל'הערה למוטי'."}
                    {/* 🆕 27.10 — היכרות עם המערכת: הצעה בשיחה ריקה, מכל מסך */}
                    {sysTour && (
                      <div className="tf-mact">
                        <button className="btn btn-secondary btn-sm" onClick={() => startTour("all", sysTour)}>
                          ✦ היכרות קצרה עם כל המערכת
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {!showChats && log.map(msgView)}
                {!showChats && busy === "think" && <div className="tf-think">תפארת חושבת…</div>}
                {!showChats && !chatOwner && s!.brain && hasChat && !busy && !log[log.length - 1]?.actions?.some((a) => a.type === "note") && (
                  <button type="button" className="tf-lognote" onClick={() => void logAsNote()}>📝 לרשום את השיחה הזו כהערה למוטי</button>
                )}
                {!showChats && !hasChat && !busy && suggestions.length > 0 && (
                  <div className="tf-sugg">
                    {suggestions.map((q) => <button key={q} className="tag" onClick={() => void ask(q)}>{q}</button>)}
                  </div>
                )}
                <div ref={logEnd} />
              </div>

              {s!.brain ? (
                <form className="tf-compose" onSubmit={(e) => { e.preventDefault(); void ask(draft); }}>
                  <label htmlFor="tf-q" className="visually-hidden">שאלה לתפארת</label>
                  <textarea id="tf-q" rows={1} value={draft} placeholder={chatOwner ? "לקריאה בלבד — להמשך: שיחה חדשה" : "אפשר לשאול אותי כל דבר על המערכת"}
                            disabled={busy === "listen" || busy === "stt" || !!chatOwner}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void ask(draft); } }} />
                  <button type="button" className={`tf-mic${busy === "listen" ? " is-rec" : ""}`} disabled={busy === "think" || busy === "stt" || busy === "note" || !!chatOwner}
                          aria-label={busy === "listen" ? "סיימתי — לשלוח" : "לדבר איתי"} title={busy === "listen" ? "סיימתי — לשלוח" : "לדבר איתי"}
                          onClick={() => void startRec()}>
                    {/* 📄 מוטי 30.09: "אייקון המיקרופון קטן מידי עד שהוא כמעט ולא מזוהה" ⇒ SVG בצבע המערכת (⛔ אימוג'י — קטן ואפור בווינדוס). */}
                    {busy === "listen" ? (
                      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2.5" fill="currentColor" /></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="2.5" width="6" height="12" rx="3" fill="currentColor" stroke="none" />
                        <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
                        <path d="M12 17.5V21" />
                        <path d="M8.5 21h7" />
                      </svg>
                    )}
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={!draft.trim() || !!busy}>שליחה</button>
                </form>
              ) : (
                <p className="muted small tf-soon">בקרוב: אפשר יהיה לשאול אותי כל שאלה על המערכת, בכתב או בקול.</p>
              )}
              </div>
            </div>
          ) : (
            <div className="tf-slot" ref={setSlot} />
          )}
          </>)}
        </div>
      )}

      {run && !run.paused && (
        <TourRunner key={`${run.tour.tool}-${run.mode}-${run.index}`} tour={run.tour} steps={run.steps} start={run.index}
                    point={run.mode === "point"} hasVoice={s!.voice} onClose={(finished) => endTour(finished)} onCheck={onCheck}
                    onDeep={deepTour} />
      )}

      {/* כלי ההערות — טעון תמיד (טיוטה · צרופות · צילום בעריכה), מוצג בתוך החלון במצב "הערה למוטי".
          🔑 סגירה מתוכו (Esc · צילום מסך) רק מסתירה את החלון — הצילום פותח אותו שוב בסוף. */}
      <FeedbackButton hideButton embedded slot={mode === "note" ? slot : null} open={panel && mode === "note"}
                      onOpenChange={(v) => { if (v) openPanel("note"); else setPanel(false); }}
                      prefill={prefill} onUnseen={setUnseen}
                      onSent={(fid) => {
                        if (pendingCheck) {
                          void saveProgress({ tool: pendingCheck.tool, check: { step: pendingCheck.step, ok: false, feedback_id: fid } });
                          setPendingCheck(null);
                          window.setTimeout(resumeAfterCheck, 900);          // "נשלח ✓" לרגע, והסיור ממשיך
                        }
                      }} />
    </>
  );
}
